import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { Button, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useDebounceValue } from 'usehooks-ts';
import { useCreateEvaSyncRun, useEvaSyncPreflight } from '@/hooks/organization-programs';
import {
  EVA_SYNC_MPA_BULLETIN_NOT_CONFIGURED_MESSAGE,
  getApiErrorMessage,
  isMpaBulletinNotConfiguredPreflightError,
  shouldConfirmEvaSyncWithoutBulletin,
} from '@/shared/utils';
import { EVA_SYNC_BULLETIN_MODE } from '@/types';
import styles from './EvaSyncForm.module.sass';

const DEBOUNCE_MS = 500;

const CONTENT = {
  EVA_PUBLIC_ID_LABEL: 'EVA Public ID',
  EVA_PUBLIC_ID_PLACEHOLDER: 'Enter church public ID from old EVA',
  SYNC_BUTTON: 'Sync',
  PREFLIGHT_CHURCH_PREFIX: 'EVA church:',
  PREFLIGHT_ERROR_FALLBACK: 'Preflight check failed. Verify the public ID and try again.',
  BULLETIN_NOT_CONFIGURED_MODAL_TITLE: 'Bulletin not configured in MPA',
  BULLETIN_NOT_CONFIGURED_MODAL_LINE_1_EVA_ENABLED:
    'Bulletin is enabled in Eva, but Bulletin is not configured in MPA.',
  BULLETIN_NOT_CONFIGURED_MODAL_LINE_1_MPA_ONLY: 'Bulletin is not configured in MPA.',
  BULLETIN_NOT_CONFIGURED_MODAL_LINE_2: 'Do you want to continue sync without Bulletin?',
  BULLETIN_NOT_CONFIGURED_MODAL_CANCEL: 'Cancel',
  BULLETIN_NOT_CONFIGURED_MODAL_CONFIRM: 'Continue without Bulletin',
};

type EvaSyncFormProps = {
  churchId: string;
};

const EvaSyncForm = ({ churchId }: EvaSyncFormProps) => {
  const [evaPublicId, setEvaPublicId] = useState('');
  const [debouncedPublicId] = useDebounceValue(evaPublicId.trim(), DEBOUNCE_MS);

  const {
    mutate: runPreflight,
    reset: resetPreflight,
    data: preflightData,
    isPending: isPreflightPending,
    isSuccess: isPreflightSuccess,
    isError: isPreflightError,
    error: preflightError,
  } = useEvaSyncPreflight(churchId);

  const { mutate: createSyncRun, isPending: isSyncPending } = useCreateEvaSyncRun(churchId);

  const handleSyncSuccess = useCallback(() => {
    setEvaPublicId('');
    resetPreflight();
  }, [resetPreflight]);

  useEffect(() => {
    if (!debouncedPublicId) {
      resetPreflight();
      return;
    }

    runPreflight({ evaPublicId: debouncedPublicId });
  }, [debouncedPublicId, resetPreflight, runPreflight]);

  const trimmedPublicId = evaPublicId.trim();
  const isInputSynced = trimmedPublicId === debouncedPublicId;
  const isBulletinNotConfiguredError = isMpaBulletinNotConfiguredPreflightError(preflightError);
  const shouldConfirmWithoutBulletin = shouldConfirmEvaSyncWithoutBulletin({
    preflightData,
    preflightError,
  });
  const isPreflightReady = isPreflightSuccess && isInputSynced && !!preflightData && !isPreflightPending;
  const canSync =
    !!debouncedPublicId && isInputSynced && !isPreflightPending && (isPreflightReady || isBulletinNotConfiguredError);
  const showPreflightLoading = !!debouncedPublicId && isPreflightPending;
  const showPreflightError =
    isPreflightError && isInputSynced && !!debouncedPublicId && !isPreflightPending && !isBulletinNotConfiguredError;
  const showBulletinNotConfiguredNotice =
    isBulletinNotConfiguredError && isInputSynced && !!debouncedPublicId && !isPreflightPending;
  const showPreflightSuccess = isPreflightReady && !!preflightData;
  const handleEvaPublicIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEvaPublicId(event.currentTarget.value);
  };

  const runSyncWithBulletinMode = useCallback(
    (bulletinMode: EVA_SYNC_BULLETIN_MODE) => {
      if (!debouncedPublicId) {
        return;
      }

      createSyncRun(
        {
          evaPublicId: debouncedPublicId,
          bulletinMode,
        },
        { onSuccess: handleSyncSuccess }
      );
    },
    [createSyncRun, debouncedPublicId, handleSyncSuccess]
  );

  const handleBulletinNotConfiguredConfirm = useCallback(() => {
    runSyncWithBulletinMode(EVA_SYNC_BULLETIN_MODE.SKIP);
  }, [runSyncWithBulletinMode]);

  const openBulletinNotConfiguredModal = useCallback(() => {
    const isEvaBulletinEnabled = preflightData?.bulletin.evaEnabled ?? isBulletinNotConfiguredError;
    const modalLine1 = isEvaBulletinEnabled
      ? CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_LINE_1_EVA_ENABLED
      : CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_LINE_1_MPA_ONLY;

    modals.openConfirmModal({
      title: CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_TITLE,
      children: (
        <Stack gap="xs">
          <Text size="sm">{modalLine1}</Text>
          <Text size="sm">{CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_LINE_2}</Text>
        </Stack>
      ),
      labels: {
        confirm: CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_CONFIRM,
        cancel: CONTENT.BULLETIN_NOT_CONFIGURED_MODAL_CANCEL,
      },
      groupProps: {
        justify: 'flex-end',
        wrap: 'nowrap',
        gap: 'sm',
      },
      onConfirm: handleBulletinNotConfiguredConfirm,
    });
  }, [handleBulletinNotConfiguredConfirm, isBulletinNotConfiguredError, preflightData?.bulletin.evaEnabled]);

  const handleSyncClick = () => {
    if (!canSync || !debouncedPublicId) {
      return;
    }

    if (shouldConfirmWithoutBulletin) {
      openBulletinNotConfiguredModal();
      return;
    }

    if (!preflightData) {
      return;
    }

    const bulletinMode = preflightData.bulletin.canInclude
      ? EVA_SYNC_BULLETIN_MODE.INCLUDE
      : EVA_SYNC_BULLETIN_MODE.SKIP;

    runSyncWithBulletinMode(bulletinMode);
  };

  return (
    <Stack gap="xs" className={styles.form}>
      <Group align="flex-end" gap="md" wrap="nowrap" className={styles.formRow}>
        <TextInput
          className={styles.publicIdInput}
          label={CONTENT.EVA_PUBLIC_ID_LABEL}
          placeholder={CONTENT.EVA_PUBLIC_ID_PLACEHOLDER}
          value={evaPublicId}
          onChange={handleEvaPublicIdChange}
          disabled={isSyncPending}
        />
        <Group gap="xs" align="center" wrap="nowrap" className={styles.actions}>
          <Button onClick={handleSyncClick} disabled={!canSync} loading={isSyncPending}>
            {CONTENT.SYNC_BUTTON}
          </Button>
          {showPreflightLoading && <Loader size="sm" aria-label="Running preflight check" />}
        </Group>
      </Group>
      {showPreflightSuccess && (
        <Text size="sm" c="dimmed">
          {CONTENT.PREFLIGHT_CHURCH_PREFIX} {preflightData.evaChurch.name}
        </Text>
      )}
      {showBulletinNotConfiguredNotice && (
        <Text size="sm" c="orange">
          {getApiErrorMessage({
            error: preflightError,
            fallback: EVA_SYNC_MPA_BULLETIN_NOT_CONFIGURED_MESSAGE,
          })}
        </Text>
      )}
      {showPreflightError && (
        <Text size="sm" c="red">
          {getApiErrorMessage({ error: preflightError, fallback: CONTENT.PREFLIGHT_ERROR_FALLBACK })}
        </Text>
      )}
    </Stack>
  );
};

export default EvaSyncForm;
